/**
 * Client IP / geolocation lookup for the admin area.
 *
 * Exposes three globals:
 *  - window.formychat_ip                   Raw API payload, or null on failure.
 *  - window.formychat_country_code         Dial code string, always set, never undefined.
 *  - window.formychat_country_code_ready   Promise<string> resolving to the dial code.
 *
 * The promise exists because the `formychat_ip_loaded` event can fire before a
 * Vue component mounts, in which case a listener registered in onMounted would
 * miss it entirely. A promise can be awaited at any time, even after it settled.
 */
(() => {

    const CACHE_KEY = 'formychat_ip_data';
    const DAY = 24 * 60 * 60 * 1000;
    const FALLBACK_COUNTRY_CODE = '44';

    // Resolver handle for window.formychat_country_code_ready.
    let resolveCountryCode;
    window.formychat_country_code_ready = new Promise((resolve) => {
        resolveCountryCode = resolve;
    });

    // Never undefined, so consumers can read it synchronously without guarding.
    window.formychat_country_code = FALLBACK_COUNTRY_CODE;

    /**
     * Publish the resolved dial code to every consumer: global, promise, event.
     *
     * @param {object|null} data IP payload, or null when lookup failed.
     */
    const publish = (data) => {
        const code = String(data?.country_phone || '')
            .replace('+', '')
            .trim() || FALLBACK_COUNTRY_CODE;

        window.formychat_country_code = code;

        // Settling twice is a no-op, so repeat calls are harmless.
        resolveCountryCode(code);

        window.dispatchEvent(new CustomEvent('formychat_ip_loaded', {
            detail: data || {},
        }));
    };

    /**
     * Ask our own REST endpoint for the country, which resolves it server side.
     *
     * Required because ipwhois.app now answers browser requests with HTTP 403
     * ("CORS is not supported on the Free plan"), so a direct call from the
     * admin page can never succeed. WordPress is not subject to CORS.
     *
     * @return {Promise<string>} Dial code, or an empty string when unresolved.
     */
    const fetchFromServer = async () => {
        // Admin pages localize formychat_common_vars; the public widget
        // localizes formychat_vars. Either provides the endpoint.
        const endpoint = window.formychat_common_vars?.geo_endpoint
            || window.formychat_vars?.geo_endpoint;

        if (!endpoint) {
            return null;
        }

        const nonce = window.formychat_common_vars?.rest_nonce
            || window.formychat_vars?.rest_nonce
            || '';

        try {
            const res = await fetch(endpoint, {
                headers: nonce ? { 'X-WP-Nonce': nonce } : {},
            });

            if (!res.ok) {
                return null;
            }

            const data = await res.json();
            if (!data || !data.dial_code) {
                return null;
            }

            // Shape the response into the field names consumers already read.
            return {
                country_phone: String(data.dial_code),
                country_code: data.country_code || '',
                country: data.country || '',
                city: data.city || '',
                region: data.region || '',
                timezone_gmt: data.timezone || '',
                currency: data.currency || '',
                isp: data.isp || '',
                org: data.org || '',
                latitude: data.latitude || 0,
                longitude: data.longitude || 0,
                ip: data.ip || '',
            };
        } catch (e) {
            return null;
        }
    };

    /**
     * Resolve the client country, using a one day localStorage cache.
     *
     * @return {Promise<object|null>} IP payload, or null when unresolved.
     */
    const loadClientInformation = async () => {
        // Serve from cache when it is still fresh.
        try {
            const cached = localStorage.getItem(CACHE_KEY);
            if (cached) {
                const { timestamp, data } = JSON.parse(cached);
                if (data && Date.now() - timestamp < DAY) {
                    window.formychat_ip = data;
                    publish(data);
                    return data;
                }
            }
        } catch (e) {
            // Corrupt cache entry or storage disabled. Fall through to lookup.
        }

        const data = await fetchFromServer();

        if (!data) {
            // Unresolved (local IP, API down, endpoint unreachable). Publish the
            // fallback so awaiting callers are never left hanging, and do not
            // cache, so the next page load retries.
            window.formychat_ip = null;
            publish(null);
            return null;
        }

        try {
            localStorage.setItem(CACHE_KEY, JSON.stringify({
                timestamp: Date.now(),
                data,
            }));
        } catch (e) {
            // Storage full or unavailable. Not fatal.
        }

        window.formychat_ip = data;
        publish(data);
        return data;
    };

    // Start immediately. The lookup needs the network, not the DOM, and the
    // script is enqueued in the head, so this widens the head start before Vue
    // mounts and asks for the value.
    loadClientInformation();
})()
